import { Alert, Box, Typography } from '@mui/material';

import type { LegacyCronMigrationAnalysis } from '../utils/legacyCronMigration';
import { formatFireInstantLabel } from '../utils/legacyCronMigration';

interface CronScheduleMigrationNoticeProps {
  migration: LegacyCronMigrationAnalysis;
  compact?: boolean;
}

export const CronScheduleMigrationNotice = ({
  migration,
  compact = false,
}: CronScheduleMigrationNoticeProps) => {
  return (
    <Alert severity="warning" sx={{ mb: compact ? 1 : 1.5 }}>
      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
        This schedule uses a legacy cron format
      </Typography>
      <Typography variant="body2" sx={{ mb: 1 }}>
        The stored expression does not match what the builder would save today. Saving will update
        to the corrected expression unless you only change unrelated fields (such as template name).
      </Typography>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>
        Stored (legacy)
      </Typography>
      <Box
        component="code"
        sx={{
          display: 'block',
          fontFamily: 'monospace',
          fontSize: '0.8rem',
          mb: 1,
          wordBreak: 'break-all',
        }}
      >
        {migration.storedExpression}
      </Box>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>
        After save
      </Typography>
      <Box
        component="code"
        sx={{
          display: 'block',
          fontFamily: 'monospace',
          fontSize: '0.8rem',
          mb: 1,
          wordBreak: 'break-all',
        }}
      >
        {migration.migratedExpression}
      </Box>

      {!compact && migration.storedFireInstants.length > 0 && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>
            Fire times — stored (legacy) → after save
          </Typography>
          {migration.migratedFireInstants.map((migrated, index) => {
            const stored = migration.storedFireInstants[index];
            return (
              <Typography key={`migration-fire-${index}`} variant="body2">
                {stored ? formatFireInstantLabel(stored) : '—'} → {formatFireInstantLabel(migrated)}
              </Typography>
            );
          })}
        </Box>
      )}
    </Alert>
  );
};
